/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect, useRef } from "react";
import type { ReactElement } from "react";

import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";
import {
  subscribeStandalonePlaybackListener,
  unsubscribeStandalonePlaybackListener
} from "../../viewModel/videoPlayerUi";
import {
  useUnityDispatch,
  useUnitySelector
} from "../../viewModel/UnityAppStore";
import { selectIsEasVideoVisible } from "../../viewModel/osd";

import "./VideoPlayer.scss";

/**
 * Always-mounted video player container component.
 * Renders a single <video> element and container div that persists across
 * all route transitions. View mode switching (FullScreen, PIP, PIG,
 * Atmospheric, BrowserFullScreen) is handled via CSS class manipulation
 * by VideoManager — no React re-renders needed for mode transitions.
 *
 * PIP uses the native browser Picture-in-Picture window, so the in-page
 * container requires no drag handling.
 *
 * A second <video> is rendered alongside it for standalone media — a
 * progressive file such as an EAS alert tone, played outside the Shaka
 * pipeline. It is a separate element on purpose: Shaka binds its own DOM
 * listeners and a MediaSource to the main one, so sharing it would both feed
 * bogus state into main playback and leave the main stream unrecoverable
 * without a re-tune. It sits outside the container div so the view-mode
 * classes never reach it.
 *
 * This component should be mounted at the app root level (outside BrowserRouter)
 * and destroyed only when the app itself is destroyed.
 */
export function VideoPlayer(): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const standaloneRef = useRef<HTMLVideoElement>(null);
  const dispatch = useUnityDispatch();

  // An EAS alert carrying video needs this element to have a surface to paint
  // on. It is collapsed for every other alert — see VideoPlayer.scss for why
  // it is collapsed rather than hidden.
  const isEasVideoVisible: boolean = useUnitySelector(selectIsEasVideoVisible);

  // Kept on its own empty-dependency effect: configurePlayer() runs
  // initPlayer(), which subscribes the controller to its managers and never
  // unsubscribes, so a second run would duplicate every player event.
  useEffect(() => {
    const containerEl = containerRef.current;
    const videoEl = videoRef.current;
    const standaloneEl = standaloneRef.current;

    if (containerEl !== null && videoEl !== null) {
      VideoPlaybackController.getInstance().configurePlayer(
        videoEl,
        containerEl,
        standaloneEl !== null ? standaloneEl : undefined
      );
    }
  }, []);

  // Subscribed here rather than on the player screen: an emergency alert must
  // not be suppressed by whatever the user is currently watching, so the
  // listener has to outlive any single route.
  useEffect(() => {
    dispatch(subscribeStandalonePlaybackListener());

    return () => {
      dispatch(unsubscribeStandalonePlaybackListener());
    };
  }, [dispatch]);

  return (
    <>
      {/*
        VoiceView: the video surface is visual only. aria-hidden + tabIndex -1
        keep the <video> (and Shaka's caption container, which Shaka renders
        inside this div) out of the accessibility tree and out of focus, so
        VoiceView never reads, or keeps re-reading, anything about the video
        while it plays. The player's controls carry the accessible names.
      */}
      <div
        className="video-player-container hidden"
        ref={containerRef}
        aria-hidden="true"
      >
        <video ref={videoRef} tabIndex={-1} aria-hidden="true" />
      </div>
      {/*
        The track carries no cues and is here to satisfy the captions
        accessibility rule. The accessible form of an EAS alert is its text,
        which the alert overlay presents alongside this media whether or not
        the media itself carries a picture.
      */}
      <video
        ref={standaloneRef}
        tabIndex={-1}
        aria-hidden="true"
        className={
          isEasVideoVisible
            ? "standalone-media-player standalone-media-player--visible"
            : "standalone-media-player"
        }
      >
        <track kind="captions" />
      </video>
    </>
  );
}
